import type { CSSProperties, FC } from 'react'
import type { Project } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { clamp } from '@/lib/frame'
import { useMediaQuery } from '@/hooks/use-media-query'
import { useScrollFrame } from '@/hooks/use-scroll-frame'
import { Phone } from '@/components/kit/phone'
import { Photo } from '@/components/kit/photo'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { FeedScreen } from '@/components/work/feed-screen'

const tilts = [-3, 2.5, -1.5, 3, -2.5, 1.5]

/**
 * The case study's posts in phone frames. On large screens the row is pinned
 * and slides sideways as the page scrolls; elsewhere it is a swipeable row.
 */
export const PhoneGallery: FC<{ project: Project }> = ({ project }) => {
    const sectionRef = useRef<HTMLElement>(null)
    const viewportRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const distanceRef = useRef(0)
    const [distance, setDistance] = useState(0)
    const pinned = useMediaQuery('(min-width: 1024px) and (min-height: 640px)')
    const handle = `@${project.slug.replace(/-/g, '.')}`

    const uploads = project.gallery.map((src, index) => ({ src, alt: project.gallery_alt[index] ?? '' }))
    const count = uploads.length || project.feed.length

    useEffect(() => {
        const viewport = viewportRef.current
        const track = trackRef.current

        if (!pinned || !viewport || !track) return

        const measure = () => {
            const next = Math.max(0, Math.round(track.offsetWidth - viewport.clientWidth))

            distanceRef.current = next
            setDistance(next)
        }

        const observer = new ResizeObserver(measure)

        observer.observe(track)
        observer.observe(viewport)
        measure()

        return () => {
            observer.disconnect()
            track.style.transform = ''
        }
    }, [pinned])

    useScrollFrame(() => {
        const section = sectionRef.current
        const track = trackRef.current

        if (!section || !track) return

        const rect = section.getBoundingClientRect()
        const total = rect.height - window.innerHeight
        const progress = total > 0 ? clamp(-rect.top / total) : 0

        track.style.transform = `translate3d(${(-progress * distanceRef.current).toFixed(1)}px, 0, 0)`
        section.style.setProperty('--gallery-progress', progress.toFixed(4))
    }, pinned)

    if (!count) return null

    return (
        <section
            ref={sectionRef}
            aria-labelledby='gallery-title'
            className='relative bg-sand'
            style={pinned && distance ? { height: `calc(100vh + ${distance}px)` } : undefined}
        >
            <div className='py-20 md:py-28 pin:sticky pin:top-0 pin:flex pin:h-screen pin:flex-col pin:justify-center pin:overflow-hidden pin:py-0'>
                <div className='mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-5 md:flex-row md:items-end md:justify-between md:px-10'>
                    <Reveal>
                        <SectionLabel>On the phone</SectionLabel>
                        <h2
                            id='gallery-title'
                            className='mt-5 text-[clamp(2.5rem,5vw,4.5rem)] font-bold leading-[0.92] tracking-[-0.04em]'
                        >
                            The posts that <span className='font-serif font-normal italic tracking-[-0.01em]'>did it</span>
                        </h2>
                    </Reveal>
                    <Reveal
                        as='p'
                        delay={150}
                        className='max-w-xs text-base leading-relaxed text-stone'
                    >
                        Swipe through a handful of the real posts behind the numbers, as they looked on the day they went out.
                    </Reveal>
                </div>

                <div
                    ref={viewportRef}
                    className='no-scrollbar mt-10 snap-x snap-mandatory overflow-x-auto pin:mt-8 pin:snap-none pin:overflow-visible'
                >
                    <div
                        ref={trackRef}
                        className='flex w-max items-center gap-6 px-5 pb-8 pt-4 will-change-transform md:gap-8 md:px-10 xl:px-[max(2.5rem,calc((100vw_-_1400px)/2_+_2.5rem))]'
                    >
                        {(uploads.length ? uploads : project.feed).map((item, index) => (
                            <div
                                key={index}
                                className='w-[230px] shrink-0 snap-center sm:w-[260px] pin:w-[min(19vw,calc((100vh_-_250px)*0.47))]'
                                style={{ rotate: `${tilts[index % tilts.length]}deg` } as CSSProperties}
                            >
                                <Phone>
                                    {'src' in item ? (
                                        <Photo
                                            src={item.src}
                                            alt={item.alt}
                                            sizes='300px'
                                            className='absolute inset-0'
                                        />
                                    ) : (
                                        <FeedScreen
                                            post={item}
                                            handle={handle}
                                            index={index}
                                            total={count}
                                        />
                                    )}
                                </Phone>
                            </div>
                        ))}
                    </div>
                </div>

                <div
                    aria-hidden='true'
                    className='mx-auto hidden w-full max-w-[1400px] px-10 pin:block'
                >
                    <span className='block h-px bg-ink/15'>
                        <span
                            className='block h-full origin-left bg-ember'
                            style={{ transform: 'scaleX(var(--gallery-progress, 0))' }}
                        />
                    </span>
                </div>
            </div>
        </section>
    )
}
